<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants.js';

defineProps({
  icon: { type: [String, Object], required: true },
  iconBgColor: { type: String, default: 'bg-n-alpha-3' },
  senderTranslationKey: { type: String, required: true },
  content: { type: String, required: true },
  title: { type: String, default: '' }, // Title can be any name, description, etc
  action: {
    type: Object,
    required: true,
    validator: action => {
      return action.label && (action.href || action.onClick);
    },
  },
});

const { sender, variant } = useMessageContext();
const { t } = useI18n();

const senderName = computed(() => {
  return sender?.value?.name || '';
});

const darkSurfaceVariants = [
  MESSAGE_VARIANTS.AGENT,
  MESSAGE_VARIANTS.BOT,
  MESSAGE_VARIANTS.TEMPLATE,
];

const isDarkSurfaceVariant = computed(() =>
  darkSurfaceVariants.includes(variant.value)
);

const senderTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/80' : 'text-n-slate-12';
});

const titleTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white' : 'text-n-slate-12';
});

const contentTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/80' : 'text-n-slate-11';
});

const actionColorClass = computed(() => {
  return isDarkSurfaceVariant.value
    ? 'bg-white/10 text-white border-white/20 hover:bg-white/20'
    : 'bg-n-solid-3 text-n-slate-12 border-n-container hover:bg-n-solid-4/60';
});
</script>

<template>
  <BaseBubble class="overflow-hidden p-3" data-bubble-name="attachment">
    <div class="grid gap-4 min-w-64">
      <div class="grid gap-3">
        <div
          class="size-8 rounded-lg grid place-content-center"
          :class="iconBgColor"
        >
          <slot name="icon">
            <Icon :icon="icon" class="text-white size-4" />
          </slot>
        </div>
        <div class="space-y-1 overflow-hidden">
          <div
            v-if="senderName"
            class="text-sm truncate"
            :class="senderTextClass"
          >
            {{
              t(senderTranslationKey, {
                sender: senderName,
              })
            }}
          </div>
          <slot>
            <div v-if="title" class="truncate text-sm" :class="titleTextClass">
              {{ title }}
            </div>
            <div
              v-if="content"
              class="truncate text-sm"
              :class="contentTextClass"
            >
              {{ content }}
            </div>
          </slot>
        </div>
      </div>
      <div v-if="action" class="mb-2">
        <a
          v-if="action.href"
          :href="action.href"
          rel="noreferrer noopener nofollow"
          target="_blank"
          class="w-full block px-4 py-2 rounded-lg text-sm text-center border transition-colors duration-150"
          :class="actionColorClass"
        >
          {{ action.label }}
        </a>
        <button
          v-else
          class="w-full px-4 py-2 rounded-lg text-sm text-center border transition-colors duration-150"
          :class="actionColorClass"
          @click="action.onClick"
        >
          {{ action.label }}
        </button>
      </div>
    </div>
  </BaseBubble>
</template>
